/**
 * @module native
 * @package @geenius/adapters/react-native
 * @description Lazily resolves React Native primitives so the package subpath
 * can be imported by Node-based export smoke tests without parsing the
 * `react-native` package's Flow source.
 */

import { AdapterInvariantError } from "@geenius/adapters";
import { type ComponentType, createElement, type ReactElement } from "react";
import type {
  ActivityIndicatorProps,
  FlatListProps,
  ModalProps,
  PressableProps,
  ScrollViewProps,
  StyleProp,
  TextInputProps,
  TextProps,
  ViewProps,
} from "react-native";

type NativeComponent<P extends object> = ComponentType<P>;

interface NativeRuntime {
  ActivityIndicator: NativeComponent<ActivityIndicatorProps>;
  FlatList: <ItemT>(props: FlatListProps<ItemT>) => ReactElement | null;
  Modal: NativeComponent<ModalProps>;
  Pressable: NativeComponent<PressableProps>;
  ScrollView: NativeComponent<ScrollViewProps>;
  Text: NativeComponent<TextProps>;
  TextInput: NativeComponent<TextInputProps>;
  View: NativeComponent<ViewProps>;
}

type ModuleRequire = (specifier: string) => unknown;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isComponent(value: unknown): boolean {
  return (
    typeof value === "function" || (typeof value === "object" && value !== null)
  );
}

function isNativeRuntime(value: unknown): value is NativeRuntime {
  return (
    isRecord(value) &&
    isComponent(value.ActivityIndicator) &&
    isComponent(value.Modal) &&
    isComponent(value.Pressable) &&
    isComponent(value.ScrollView) &&
    isComponent(value.Text) &&
    isComponent(value.TextInput) &&
    isComponent(value.View) &&
    isComponent(value.FlatList)
  );
}

function getRequire(): ModuleRequire | undefined {
  const globalWithRequire = globalThis as { require?: ModuleRequire };
  if (typeof globalWithRequire.require === "function") {
    return globalWithRequire.require;
  }

  return undefined;
}

function resolveRuntime(): NativeRuntime {
  const globalOverride = (
    globalThis as {
      __GEENIUS_REACT_NATIVE_RUNTIME__?: unknown;
    }
  ).__GEENIUS_REACT_NATIVE_RUNTIME__;
  if (isNativeRuntime(globalOverride)) {
    return globalOverride;
  }

  const runtimeRequire = getRequire();
  if (runtimeRequire) {
    try {
      const candidate = runtimeRequire("react-native");
      if (isNativeRuntime(candidate)) {
        return candidate;
      }
    } catch {
      // Fall through to the DOM test runtime when available.
    }
  }

  throw new AdapterInvariantError(
    "@geenius/adapters/react-native requires the react-native runtime to render native components. Tests and Storybook may provide globalThis.__GEENIUS_REACT_NATIVE_RUNTIME__.",
  );
}

function createNativeComponent<P extends object>(
  name: keyof NativeRuntime,
): ComponentType<P> {
  const NativeComponent = (props: P) => {
    const Component = resolveRuntime()[name] as unknown as ComponentType<P>;
    return createElement(Component, props);
  };
  NativeComponent.displayName = `GeeniusNative${String(name)}`;
  return NativeComponent;
}

export const ActivityIndicator =
  createNativeComponent<ActivityIndicatorProps>("ActivityIndicator");
export function FlatList<ItemT>(
  props: FlatListProps<ItemT>,
): ReactElement | null {
  const Component = resolveRuntime().FlatList as unknown as ComponentType<
    FlatListProps<ItemT>
  >;
  return createElement(Component, props);
}
export const Modal = createNativeComponent<ModalProps>("Modal");
export const Pressable = createNativeComponent<PressableProps>("Pressable");
export const ScrollView = createNativeComponent<ScrollViewProps>("ScrollView");
export const Text = createNativeComponent<TextProps>("Text");
export const TextInput = createNativeComponent<TextInputProps>("TextInput");
export const View = createNativeComponent<ViewProps>("View");

function flattenStyle<T>(style?: StyleProp<T>): T | undefined {
  if (!style) {
    return undefined;
  }

  if (!Array.isArray(style)) {
    return style as T;
  }

  let merged: Record<string, unknown> | undefined;
  for (const entry of style) {
    const flattened = flattenStyle(entry as StyleProp<T>);
    if (!flattened) {
      continue;
    }
    if (isRecord(flattened)) {
      merged = { ...(merged ?? {}), ...flattened };
      continue;
    }
    return flattened;
  }

  return merged as T | undefined;
}

export const StyleSheet = {
  create<T extends Record<string, unknown>>(styles: T): T {
    return styles;
  },
  flatten<T>(style?: StyleProp<T>): T | undefined {
    return flattenStyle(style);
  },
};
